// RecentUpdates.js - Global log of completed bugs/features
import React from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${THEME.spacing.xxl};
  color: ${THEME.textSecondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${THEME.spacing.xxl};
  color: ${THEME.textSecondary};
`;

const UpdateItem = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
  padding: ${THEME.spacing.md};
  background: ${THEME.inputBackground};
  border-radius: ${THEME.borderRadiusMedium};
  border-left: 3px solid ${props => props.$type === 'bug' ? THEME.success : THEME.info};
`;

const UpdateDate = styled.div`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.sm};
  white-space: nowrap;
  min-width: 70px;
`;

const UpdateContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UpdateTitle = styled.div`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  font-weight: 500;
`;

const UpdateMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.sm};
`;

const UpdateType = styled.span`
  color: ${props => props.$type === 'bug' ? THEME.success : THEME.info};
  font-size: ${THEME.fontSize.sm};
`;

const UpdateStatus = styled.span`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.sm};
`;

const Description = styled.p`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.md};
  margin: 0 0 ${THEME.spacing.lg} 0;
  line-height: 1.5;
`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const RecentUpdates = ({ updates, loading }) => {
  if (loading) {
    return <LoadingState>Loading updates...</LoadingState>;
  }

  return (
    <Container>
      <Description>
        See what bugs have been fixed and features have been added recently.
        Your feedback helps make GChain better!
      </Description>

      {updates.length === 0 ? (
        <EmptyState>
          No updates yet. Check back soon!
        </EmptyState>
      ) : (
        updates.map(update => (
          <UpdateItem key={update.id} $type={update.type}>
            <UpdateDate>{formatDate(update.completed_at)}</UpdateDate>
            <UpdateContent>
              <UpdateTitle>{update.title}</UpdateTitle>
              <UpdateMeta>
                <UpdateType $type={update.type}>
                  {update.type === 'bug' ? 'Bug fix' : 'Feature'}
                </UpdateType>
                <UpdateStatus>· {update.completion_message}</UpdateStatus>
              </UpdateMeta>
            </UpdateContent>
          </UpdateItem>
        ))
      )}
    </Container>
  );
};

export default RecentUpdates;
